<template>
  <div class="app-container asset-todo-page">
    <el-row :gutter="12" class="todo-summary">
      <el-col v-for="item in summaryItems" :key="item.key" :span="6">
        <button type="button" class="summary-card" :class="{ active: filterType === item.key }" @click="selectType(item.key)">
          <span>{{ item.label }}</span>
          <strong>{{ item.count }}</strong>
        </button>
      </el-col>
    </el-row>

    <div class="todo-toolbar">
      <div class="toolbar-left">
        <el-input v-model="keyword" clearable placeholder="搜索单号、资产或人员" prefix-icon="el-icon-search" />
        <el-button v-if="filterType === 'return'" type="primary" :loading="submitting" :disabled="!selectedReturns.length" @click="batchAcceptReturns">
          批量确认归还{{ selectedReturns.length ? `（${selectedReturns.length}）` : '' }}
        </el-button>
      </div>
      <el-button icon="el-icon-refresh" @click="loadList">刷新</el-button>
    </div>

    <el-table ref="todoTable" v-loading="loading" :data="pagedRows" :row-key="todoRowKey" @selection-change="handleSelectionChange">
      <el-table-column v-if="filterType === 'return'" type="selection" width="48" :selectable="canSelect" />
      <el-table-column label="待处理类型" width="110">
        <template slot-scope="scope"><el-tag size="mini" :type="typeTag(scope.row.targetType)">{{ typeText(scope.row.targetType) }}</el-tag></template>
      </el-table-column>
      <el-table-column label="单号" min-width="190">
        <template slot-scope="scope">
          <el-button type="text" class="order-link" :loading="detailLoadingId === todoRowKey(scope.row)" @click="openTodoDetail(scope.row)">{{ scope.row.targetNo || '查看详情' }}</el-button>
        </template>
      </el-table-column>
      <el-table-column label="资产信息" min-width="260">
        <template slot-scope="scope">
          <div v-if="scope.row.assetCode || scope.row.assetName" class="asset-cell">
            <span class="asset-code">{{ scope.row.assetCode || '-' }}</span>
            <span class="asset-name">{{ scope.row.assetName || '-' }}</span>
          </div>
          <span v-else>{{ scope.row.toDoContent || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="提交人" width="120">
        <template slot-scope="scope">{{ scope.row.assetFromUserName || scope.row.requestedByName || '-' }}</template>
      </el-table-column>
      <el-table-column label="相关信息" min-width="170">
        <template slot-scope="scope">
          <div v-if="scope.row.targetType === 'assetReturn'" class="destination-cell">
            <span>{{ managementTypeText(scope.row.assetManagementType) }}</span>
            <small>{{ scope.row.assetToLocation || scope.row.assetStorageLocation || '-' }}</small>
          </div>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column label="提交时间" width="165"><template slot-scope="scope">{{ formatTodoTime(scope.row.createTime) }}</template></el-table-column>
      <el-table-column label="操作" width="210" fixed="right" align="center">
        <template slot-scope="scope">
          <template v-if="scope.row.targetType === 'assetReturn'">
            <el-button type="text" :disabled="submitting" @click="acceptReturn(scope.row)">确认归还入库</el-button>
            <el-button type="text" class="danger-text" :disabled="submitting" @click="rejectReturn(scope.row)">拒绝归还</el-button>
          </template>
          <el-button v-else type="text" :loading="processingRowId === scope.row.toDoId" @click="goProcess(scope.row)">{{ actionText(scope.row) }}</el-button>
        </template>
      </el-table-column>
    </el-table>
    <pagination v-show="filteredRows.length > 0" :total="filteredRows.length" :page.sync="pageNum" :limit.sync="pageSize" :page-sizes="[10]" />
    <el-empty v-if="!loading && !filteredRows.length" description="暂无待处理事项" />

    <asset-order-detail-dialog :visible.sync="detailVisible" :type="detailType" :record="detailRow || {}" @success="loadList" />

    <asset-repair-process-dialog :visible.sync="repairProcessOpen" :mode="repairProcessMode" :record="repairRecord" @success="handleRepairSuccess" />
    <asset-claim-process-dialog :visible.sync="claimProcessOpen" :record="claimRecord" @success="loadList" />

    <el-dialog title="确认盘点结果" :visible.sync="stocktakeOpen" width="520px" append-to-body :close-on-click-modal="false">
      <el-form label-width="92px">
        <el-form-item label="资产">{{ stocktakeItem.assetCodeSnapshot || '-' }} / {{ stocktakeItem.assetNameSnapshot || '-' }}</el-form-item>
        <el-form-item label="账面位置">{{ stocktakeItem.expectedLocation || '-' }}</el-form-item>
        <el-form-item label="盘点结果"><el-select v-model="stocktakeForm.result" style="width:100%"><el-option v-for="item in stocktakeResultOptions" :key="item.value" :label="item.label" :value="item.value" /></el-select></el-form-item>
        <el-form-item label="实际位置"><el-input v-model="stocktakeForm.actualLocation" maxlength="200" /></el-form-item>
        <el-form-item label="盘点说明"><el-input v-model="stocktakeForm.remark" type="textarea" :rows="3" maxlength="1000" show-word-limit /></el-form-item>
      </el-form>
      <div slot="footer"><el-button @click="stocktakeOpen = false">取消</el-button><el-button type="primary" :loading="submitting" @click="submitStocktake">提交盘点结果</el-button></div>
    </el-dialog>

    <el-dialog :title="flowAction.title" :visible.sync="flowOpen" width="560px" append-to-body :close-on-click-modal="false">
      <el-descriptions :column="1" border>
        <el-descriptions-item label="操作单号">{{ flowRow.targetNo || '-' }}</el-descriptions-item>
        <el-descriptions-item label="待办事项">{{ flowRow.toDoContent || '-' }}</el-descriptions-item>
        <el-descriptions-item label="提交时间">{{ formatTodoTime(flowRow.createTime) }}</el-descriptions-item>
      </el-descriptions>
      <el-form label-width="92px" class="flow-form">
        <el-form-item label="确认说明"><el-input v-model="flowRemark" type="textarea" :rows="3" maxlength="500" show-word-limit placeholder="选填" /></el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="flowOpen = false">取消</el-button>
        <el-button class="danger-button" :loading="submitting" @click="submitFlow(false)">{{ flowAction.reject }}</el-button>
        <el-button type="primary" :loading="submitting" @click="submitFlow(true)">{{ flowAction.approve }}</el-button>
      </div>
    </el-dialog>

    <el-dialog title="报废审批" :visible.sync="approvalOpen" width="920px" append-to-body custom-class="asset-approval-dialog">
      <div v-if="approvalRow.toDoId">
        <div class="approval-order">审批单号：SP{{ approvalRow.toDoId }}　{{ approvalRow.toDoContent || '' }}</div>
        <approve-info
          :key="approvalRow.toDoId"
          :target-id="approvalRow.toDoId"
          target-type="assetRepair"
          :allow-transfer="false"
          @success="handleApprovalSuccess"
        />
      </div>
    </el-dialog>
  </div>
</template>

<script>
import AssetRepairProcessDialog from './repairProcessDialog'
import AssetClaimProcessDialog from './claimProcessDialog'
import AssetOrderDetailDialog from './orderDetailDialog'
import ApproveInfo from '@/views/crm/components/approveInfo'
import { myToDoList } from '@/api/crm/dashBoard'
import {
  getAssetTodoList, getAssetDetail, acceptAssetReturn, listAssetRepairs, listAssetClaims,
  getMyPendingStocktakeItems, confirmAssetStocktake, confirmAssetOperation
} from '@/api/crm/asset'

export default {
  name: 'CrmAssetTodo',
  components: { AssetRepairProcessDialog, AssetClaimProcessDialog, AssetOrderDetailDialog, ApproveInfo },
  data() {
    return {
      loading: false,
      submitting: false,
      rows: [],
      selectedReturns: [],
      detailVisible: false,
      detailRow: null,
      detailType: '',
      detailLoadingId: '',
      processingRowId: null,
      repairProcessOpen: false,
      repairProcessMode: 'inspect',
      repairRecord: {},
      claimProcessOpen: false,
      claimRecord: {},
      stocktakeOpen: false,
      stocktakeItem: {},
      stocktakeForm: { itemId: null, result: 10, actualLocation: '', remark: '' },
      stocktakeResultOptions: [
        { value: 10, label: '正常' }, { value: 20, label: '位置不符' },
        { value: 30, label: '损坏' }, { value: 40, label: '丢失' }, { value: 50, label: '其他异常' }
      ],
      flowOpen: false,
      flowRow: {},
      flowRemark: '',
      flowAction: { title: '确认资产流转', approve: '确认', reject: '拒绝' },
      approvalOpen: false,
      approvalRow: {},
      filterType: 'all',
      keyword: '',
      pageNum: 1,
      pageSize: 10
    }
  },
  computed: {
    summaryItems() {
      return [
        { key: 'all', label: '全部待处理', count: this.rows.length },
        { key: 'return', label: '归还待验收入库', count: this.countByTypes(['assetReturn']) },
        { key: 'claim', label: '待处理领用', count: this.countByTypes(['assetClaim']) },
        { key: 'other', label: '其他待处理', count: this.rows.length - this.countByTypes(['assetReturn', 'assetClaim']) }
      ]
    },
    filteredRows() {
      const keyword = String(this.keyword || '').trim().toLowerCase()
      return this.rows.filter(item => {
        if (this.filterType === 'return' && item.targetType !== 'assetReturn') return false
        if (this.filterType === 'claim' && item.targetType !== 'assetClaim') return false
        if (this.filterType === 'other' && ['assetReturn', 'assetClaim'].includes(item.targetType)) return false
        if (!keyword) return true
        return [item.targetNo, item.toDoContent].some(value => String(value || '').toLowerCase().includes(keyword))
      })
    },
    pagedRows() {
      const start = (this.pageNum - 1) * this.pageSize
      return this.filteredRows.slice(start, start + this.pageSize)
    }
  },
  watch: {
    keyword() { this.pageNum = 1 }
  },
  created() { this.loadList() },
  methods: {
    loadList() {
      this.loading = true
      Promise.all([getAssetTodoList(), myToDoList({ pageNum: 1, pageSize: 500 }).catch(() => ({ rows: [] }))]).then(([assetRes, dashboardRes]) => {
        const rows = (assetRes.data || []).concat((dashboardRes.rows || []).filter(item => item.targetType === 'assetRepair'))
        const seen = new Set()
        this.rows = rows.filter(item => {
          const key = this.todoRowKey(item)
          if (seen.has(key)) return false
          seen.add(key)
          return true
        }).sort((a, b) => new Date(b.createTime || 0).getTime() - new Date(a.createTime || 0).getTime())
        this.pageNum = 1
        this.clearSelection()
        this.$emit('count-change', this.rows.length)
      }).finally(() => { this.loading = false })
    },
    countByTypes(types) { return this.rows.filter(item => types.includes(item.targetType)).length },
    todoRowKey(row) { return `${row.targetType || 'asset'}-${row.toDoId || row.targetId || ''}` },
    selectType(type) { this.filterType = type; this.pageNum = 1; this.clearSelection() },
    clearSelection() {
      this.selectedReturns = []
      this.$nextTick(() => {
        if (this.$refs.todoTable) this.$refs.todoTable.clearSelection()
      })
    },
    canSelect(row) { return row.targetType === 'assetReturn' },
    handleSelectionChange(rows) { this.selectedReturns = rows.filter(item => item.targetType === 'assetReturn') },
    openTodoDetail(row) {
      const open = (type, record) => {
        this.detailType = type
        this.detailRow = Object.assign({}, row, record || {})
        this.detailVisible = true
      }
      if (row.targetType === 'assetReturn') return open('assetReturn', row)
      this.detailLoadingId = this.todoRowKey(row)
      let request
      if (row.targetType === 'assetClaim') {
        request = listAssetClaims().then(res => {
          const record = (res.data || []).find(item => Number(item.id) === Number(row.targetId || row.toDoId))
          open('assetClaim', record || row)
        })
      } else if (['assetRepairTask', 'assetRepair'].includes(row.targetType)) {
        request = listAssetRepairs({ pageNum: 1, pageSize: 10, keyword: row.targetNo }).then(res => {
          const records = res.rows || []
          const record = records.find(item => Number(item.id) === Number(row.targetId)) || records[0]
          open(row.targetType, record || row)
        })
      } else if (row.targetType === 'assetStocktake') {
        request = getMyPendingStocktakeItems().then(res => {
          const record = (res.data || []).find(item => Number(item.id) === Number(row.toDoId))
          open('assetStocktake', record || row)
        })
      } else if (row.targetType === 'assetOperation') {
        request = getAssetDetail(row.targetId).then(res => {
          const asset = res.data || {}
          const operation = (asset.operationList || []).find(item => Number(item.id) === Number(row.toDoId)) || {}
          open('assetOperation', Object.assign({ assetCode: asset.assetCode, assetName: asset.assetName }, operation))
        })
      } else {
        request = Promise.resolve(open('assetLifecycle', row))
      }
      return request.catch(() => {
        this.msgWarning('单据详情加载失败，请刷新后重试')
      }).finally(() => { this.detailLoadingId = '' })
    },
    formatTodoTime(value) {
      if (!value) return '-'
      const date = value instanceof Date ? value : new Date(value)
      if (Number.isNaN(date.getTime())) return '-'
      const pad = number => String(number).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    },
    managementTypeText(value) { return ({ HR: '人事行政', WAREHOUSE: '仓库' })[value] || '-' },
    bookTypeText(value) { return ({ ON_BOOK: '账内', OFF_BOOK: '账外' })[value] || '-' },
    assetStatusText(value) {
      return ({ 10: '闲置', 20: '在用', 30: '维修', 40: '丢失', 50: '待报废', 60: '已报废' })[value] || '-'
    },
    typeText(type) {
      return ({ assetReturn: '归还确认', assetClaim: '领用申请', assetRepairTask: '报修报废', assetRepair: '报废审批', assetStocktake: '盘点确认', assetOperation: '流转确认' })[type] || '资产待办'
    },
    typeTag(type) {
      return ({ assetReturn: 'warning', assetClaim: 'primary', assetRepairTask: 'danger', assetRepair: 'danger', assetStocktake: 'success', assetOperation: 'info' })[type] || 'info'
    },
    actionText(row) {
      if (row.targetType === 'assetRepairTask') return String(row.toDoContent || '').startsWith('登记报废处置') ? '登记报废处置' : '处理报修'
      return ({ assetClaim: '处理领用申请', assetRepair: '审批报废申请', assetStocktake: '确认盘点', assetOperation: '确认资产流转' })[row.targetType] || '去处理'
    },
    goProcess(row) {
      if (row.targetType === 'assetClaim') return this.openClaimProcess(row)
      if (row.targetType === 'assetRepairTask') return this.openRepairProcess(row)
      if (row.targetType === 'assetRepair') return this.openApproval(row)
      if (row.targetType === 'assetStocktake') return this.openStocktake(row)
      if (row.targetType === 'assetOperation') return this.openFlow(row)
      this.msgWarning('该待处理事项暂不支持当前页面处理')
    },
    openClaimProcess(row) {
      this.processingRowId = row.toDoId
      listAssetClaims().then(res => {
        const records = res.data || []
        const record = records.find(item => Number(item.id) === Number(row.targetId || row.toDoId))
        if (!record || Number(record.status) !== 10) {
          this.msgWarning('领用申请状态已变化，请刷新后重试')
          return this.loadList()
        }
        this.claimRecord = record
        this.claimProcessOpen = true
      }).finally(() => { this.processingRowId = null })
    },
    openStocktake(row) {
      this.processingRowId = row.toDoId
      getMyPendingStocktakeItems().then(res => {
        const item = (res.data || []).find(record => Number(record.id) === Number(row.toDoId))
        if (!item) {
          this.msgWarning('盘点任务状态已变化，请刷新后重试')
          return this.loadList()
        }
        this.stocktakeItem = item
        this.stocktakeForm = { itemId: item.id, result: 10, actualLocation: item.expectedLocation || '', remark: '' }
        this.stocktakeOpen = true
      }).finally(() => { this.processingRowId = null })
    },
    submitStocktake() {
      this.submitting = true
      confirmAssetStocktake(this.stocktakeForm).then(() => {
        this.msgSuccess('盘点结果已提交')
        this.stocktakeOpen = false
        this.loadList()
      }).finally(() => { this.submitting = false })
    },
    openFlow(row) {
      const content = String(row.toDoContent || '')
      let action = { title: '确认资产流转', approve: '确认资产流转', reject: '拒绝流转' }
      if (content.includes('领用')) action = { title: '确认资产领用', approve: '确认领用', reject: '拒绝领用' }
      else if (content.includes('调拨转出')) action = { title: '确认资产转出', approve: '确认转出', reject: '拒绝转出' }
      else if (content.includes('调拨接收')) action = { title: '确认资产接收', approve: '确认接收', reject: '拒绝接收' }
      this.flowRow = row
      this.flowAction = action
      this.flowRemark = ''
      this.flowOpen = true
    },
    submitFlow(approved) {
      const submit = remark => {
        this.submitting = true
        return confirmAssetOperation({ operationId: this.flowRow.toDoId, approved, remark }).then(() => {
          this.msgSuccess(approved ? '资产流转已确认' : '已拒绝本次资产流转')
          this.flowOpen = false
          this.loadList()
        }).finally(() => { this.submitting = false })
      }
      if (approved) return submit(this.flowRemark || 'PC端确认资产流转')
      if (!String(this.flowRemark || '').trim()) return this.msgWarning('拒绝时请填写原因')
      return submit(this.flowRemark.trim())
    },
    openApproval(row) {
      this.approvalRow = row
      this.approvalOpen = true
    },
    handleApprovalSuccess() {
      this.loadList()
    },
    openRepairProcess(row) {
      this.processingRowId = row.toDoId
      listAssetRepairs({ pageNum: 1, pageSize: 10, keyword: row.targetNo }).then(res => {
        const records = res.rows || []
        const record = records.find(item => Number(item.id) === Number(row.targetId)) || records[0]
        if (!record) {
          this.msgError('报修单不存在或已处理，请刷新后重试')
          return this.loadList()
        }
        if (![10, 40, 50].includes(Number(record.status))) {
          this.msgWarning('报修单状态已变化，请刷新后重试')
          return this.loadList()
        }
        this.repairRecord = record
        this.repairProcessMode = Number(record.status) === 40 ? 'disposal' : 'inspect'
        this.repairProcessOpen = true
      }).finally(() => { this.processingRowId = null })
    },
    handleRepairSuccess() {
      this.loadList()
    },
    acceptReturn(row) {
      this.$confirm(`确认“${row.toDoContent}”已完成交接并入库？`, '确认归还入库', { type: 'warning' })
        .then(() => this.submitReturn(row, true, '管理员确认归还入库'))
        .catch(() => {})
    },
    rejectReturn(row) {
      this.$prompt('请输入拒绝原因', '拒绝归还', {
        confirmButtonText: '确认拒绝', cancelButtonText: '取消', inputType: 'textarea',
        inputValidator: value => Boolean(value && value.trim()) || '请输入拒绝原因'
      }).then(({ value }) => this.submitReturn(row, false, value.trim())).catch(() => {})
    },
    submitReturn(row, approved, remark) {
      this.submitting = true
      return acceptAssetReturn({ operationId: row.toDoId, approved, targetAssetStatus: 10, remark })
        .then(() => {
          this.msgSuccess(approved ? '资产已确认归还并入库' : '已拒绝本次归还')
          this.loadList()
        })
        .finally(() => { this.submitting = false })
    },
    batchAcceptReturns() {
      const rows = this.selectedReturns.slice()
      if (!rows.length) return this.msgWarning('请先选择待验收入库的归还资产')
      this.$confirm(`确认将选中的 ${rows.length} 项资产全部归还入库？`, '批量确认归还', { type: 'warning' })
        .then(async () => {
          this.submitting = true
          const failed = []
          for (const row of rows) {
            try {
              await acceptAssetReturn({ operationId: row.toDoId, approved: true, targetAssetStatus: 10, remark: '管理员批量确认归还入库' })
            } catch (error) {
              failed.push(row.targetNo || row.toDoContent)
            }
          }
          if (failed.length) this.msgWarning(`已完成 ${rows.length - failed.length} 项，${failed.length} 项处理失败，请刷新后重试`)
          else this.msgSuccess(`已确认 ${rows.length} 项资产归还入库`)
          this.loadList()
        })
        .catch(() => {})
        .finally(() => { this.submitting = false })
    }
  }
}
</script>

<style scoped>
.todo-summary { margin-bottom: 16px; }
.summary-card { width:100%; padding:16px 18px; border:1px solid #e4e7ed; border-radius:7px; background:#f8fafc; color:#606266; font:inherit; text-align:left; cursor:pointer; }
.summary-card strong { float:right; font-size:22px; line-height:20px; color:#303133; }
.summary-card.active { border-color:#409eff; background:#ecf5ff; color:#409eff; }
.todo-toolbar { display:flex; align-items:center; justify-content:space-between; margin-bottom:14px; }
.toolbar-left { display:flex; align-items:center; gap:10px; }
.toolbar-left .el-input { width:320px; }
.order-link { padding:0; white-space:normal; text-align:left; line-height:20px; }
.asset-cell, .destination-cell { display:flex; flex-direction:column; gap:4px; line-height:20px; }
.asset-code { color:#409eff; }
.asset-name { color:#303133; }
.destination-cell small { color:#909399; font-size:12px; }
.detail-section-title { margin:18px 0 10px; padding-left:9px; border-left:3px solid #409eff; color:#303133; font-weight:600; }
.detail-section-title:first-child { margin-top:0; }
.danger-text { color:#f56c6c; }
.danger-button { color:#f56c6c; border-color:#fbc4c4; }
.flow-form { margin-top:18px; }
.approval-order { margin:4px 0 16px; color:#606266; }
</style>
